import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import PublicPortalLayout from "../../../components/public/PublicLayout";
import { NoRealData } from "@/components/shared";
import { useTreeDetail } from "@/hooks";
import { isAuthenticated, loginPath } from "@/lib/auth";
import type { TreeApi } from "@/lib/api";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-gray-500">{label}</dt>
      <dd className="text-sm font-medium text-gray-900">{value && String(value).trim() ? value : "—"}</dd>
    </div>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <PublicPortalLayout>
      <div className="max-w-[1100px] mx-auto px-4 lg:px-6 py-8">{children}</div>
    </PublicPortalLayout>
  );
}

function TreeDossier({ tree }: { tree: TreeApi }) {
  const species = tree.species_popular_name || tree.species_scientific_name || "Espécie não identificada";
  const coords =
    tree.latitude !== null && tree.longitude !== null
      ? `${tree.latitude.toFixed(6)}, ${tree.longitude.toFixed(6)}`
      : null;
  const municipality = tree.municipality_name
    ? `${tree.municipality_name}${tree.municipality_uf ? ` - ${tree.municipality_uf}` : ""}`
    : null;

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <p className="text-xs font-mono text-gray-500">{tree.cta_code || `ID ${tree.id}`}</p>
        <h1 className="text-2xl font-semibold text-gray-900">{species}</h1>
        {tree.species_scientific_name && tree.species_popular_name && (
          <p className="text-sm italic text-gray-600">{tree.species_scientific_name}</p>
        )}
        {tree.protected_species && (
          <span className="inline-block mt-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Espécie protegida
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Identificação</h2>
          <dl className="grid grid-cols-2 gap-3">
            <Field label="Código CTA" value={tree.cta_code} />
            <Field label="Código público" value={tree.public_code} />
            <Field label="Família botânica" value={tree.botanical_family} />
            <Field label="Situação do cadastro" value={tree.status} />
            <Field label="Projeto" value={tree.project_name} />
            <Field label="Cadastrado em" value={formatDate(tree.created_at)} />
          </dl>
        </section>

        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Localização</h2>
          <dl className="grid grid-cols-2 gap-3">
            <Field label="Logradouro" value={tree.street} />
            <Field label="Bairro" value={tree.neighborhood} />
            <Field label="Município" value={municipality} />
            <Field label="Coordenadas (WGS84)" value={coords} />
            <Field label="Referência" value={tree.address_reference} />
          </dl>
        </section>

        <section className="bg-white rounded-xl border border-gray-200 p-5 md:col-span-2">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Última vistoria registrada</h2>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Data" value={formatDate(tree.latest_inspection_date)} />
            <Field label="Tipo" value={tree.latest_inspection_type} />
            <Field label="Classe de risco" value={tree.latest_risk_class} />
            <Field label="Prioridade" value={tree.latest_risk_priority} />
          </dl>
          {!tree.latest_inspection_id && <p className="text-xs text-gray-500 mt-3">Este exemplar ainda não tem vistoria.</p>}
        </section>
      </div>

      <div className="flex flex-wrap gap-3">
        <Link
          to={`/cta/${tree.id}`}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
        >
          <i className="ri-file-list-3-line" />
          Abrir ficha completa (FCA)
        </Link>
      </div>
      <p className="text-xs text-gray-500">
        Documentos, fotos, vistorias e histórico do exemplar ficam na ficha completa (FCA).
      </p>
    </div>
  );
}

function AuthenticatedDossier({ treeId }: { treeId: number }) {
  const { data: tree, loading, error } = useTreeDetail(treeId);
  if (loading) return <p className="text-sm text-gray-500">Carregando exemplar...</p>;
  if (error || !tree) {
    return (
      <NoRealData
        title="Exemplar não encontrado"
        description={error ? `Não foi possível carregar o exemplar: ${error}` : `Nenhum exemplar com identificação ${treeId}.`}
        icon="ri-error-warning-line"
        variant="page"
      />
    );
  }
  return <TreeDossier tree={tree} />;
}

export default function TreeDossierPage() {
  const { id } = useParams<{ id: string }>();
  const treeId = id && /^\d+$/.test(id) ? Number(id) : null;

  // Ainda nao existe consulta publica de exemplares: sem sessao, nada a mostrar.
  if (!isAuthenticated()) {
    return (
      <Shell>
        <NoRealData
          title="Dossiê público do exemplar arbóreo"
          description="A consulta pública de exemplares ainda não está disponível. Servidores podem entrar com a matrícula para ver o cadastro."
          icon="ri-plant-line"
          variant="page"
        />
        <div className="text-center mt-4">
          <Link to={loginPath(`/arvore/${id}/dossie`)} className="text-sm font-medium text-primary-700 hover:underline">
            Entrar
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {treeId === null ? (
        <NoRealData
          title="Identificação inválida"
          description="Informe o identificador numérico do exemplar."
          icon="ri-error-warning-line"
          variant="page"
        />
      ) : (
        <AuthenticatedDossier treeId={treeId} />
      )}
    </Shell>
  );
}
